import { Link } from "react-router-dom";
import Layout from "../../components/site/Layout";
import Seo from "../../components/Seo";
import "./Guide.css";
import "./GuidesHub.css";

const GROUPS = [
  {
    heading: "Getting started",
    items: [
      { to: "/guides/what-is-an-eps-file", t: "What Is an EPS File?", d: "The format explained — history, uses, pros and cons." },
      { to: "/guides/how-to-open-eps-files", t: "How to Open EPS Files", d: "Every way to open EPS on Windows, Mac, Linux and online." },
      { to: "/guides/open-eps-without-illustrator", t: "Open EPS Without Illustrator", d: "Free tools to view and edit EPS — no Adobe needed." },
    ],
  },
  {
    heading: "Converting EPS",
    items: [
      { to: "/guides/convert-eps-to-png", t: "Convert EPS to PNG", d: "Turn EPS into a universal, shareable image." },
      { to: "/guides/convert-eps-to-jpg", t: "Convert EPS to JPG", d: "Small, web-friendly images from EPS." },
      { to: "/guides/convert-eps-to-pdf", t: "Convert EPS to PDF", d: "Universal, print-ready documents." },
    ],
  },
  {
    heading: "Understanding the format",
    items: [
      { to: "/guides/eps-vs-svg-vs-ai-vs-pdf", t: "EPS vs SVG vs AI vs PDF", d: "Which vector format to use, and when." },
      { to: "/guides/vector-vs-raster", t: "Vector vs Raster Graphics", d: "Why some images scale forever and others blur." },
      { to: "/guides/eps-file-format-explained", t: "EPS Format Explained", d: "A technical look at how EPS works." },
      { to: "/guides/fix-eps-file-problems", t: "Fix Common EPS Problems", d: "Won't open, blurry, missing fonts? Solved." },
    ],
  },
  {
    heading: "Working with EPS files",
    items: [
      { to: "/guides/best-free-eps-editors", t: "Best Free EPS Editors in 2026", d: "Open and edit EPS files without Illustrator." },
      { to: "/guides/how-to-create-an-eps-file", t: "How to Create an EPS File", d: "Export EPS from Illustrator, Inkscape, CorelDRAW and more." },
      { to: "/guides/eps-files-for-logo-design", t: "EPS Files for Logo Design", d: "Why logos come as EPS and how to use them." },
    ],
  },
];

export default function GuidesHub() {
  return (
    <Layout>
      <Seo
        title="EPS Guides & Tutorials | EPS Viewer"
        description="Free, in-depth guides about EPS files: how to open and convert them, format comparisons, vector vs raster, and fixes for common EPS problems."
        path="/guides"
      />
      <section className="guides-hub-hero">
        <div className="container">
          <span className="eyebrow">Guides &amp; tutorials</span>
          <h1>Everything about EPS files</h1>
          <p className="section-lead">
            Clear, practical guides on opening, converting and understanding
            Encapsulated PostScript files — written for designers, marketers and
            anyone who just received an EPS.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="guide__prose guides-hub__intro">
            <h2>New to EPS? Start here</h2>
            <p>
              Most people meet EPS the same way: a designer, print shop or stock library
              sends one over, and it will not open. That is not a broken file. An EPS is
              not an image in the ordinary sense — it is a small PostScript program that
              describes how to draw the artwork, and no mainstream operating system ships
              with anything that can run it.
            </p>
            <p>
              Everything else follows from that. It is why double-clicking does nothing,
              why the preview sometimes looks blurry when the file is actually perfectly
              sharp, and why the artwork scales to any size without losing quality. If you
              only read one page here, make it{" "}
              <Link to="/guides/what-is-an-eps-file">What Is an EPS File?</Link>.
            </p>
            <p>
              If you are in a hurry, the fastest path is to{" "}
              <Link to="/">open the file in our free viewer</Link> and download it as a
              PNG, JPG or PDF. Nothing to install, and it works on phones too.
            </p>
          </div>

          {GROUPS.map((g) => (
            <div className="guides-group" key={g.heading}>
              <h2 className="guides-group__title">{g.heading}</h2>
              <div className="guides-group__grid">
                {g.items.map((it) => (
                  <Link key={it.to} to={it.to} className="hubcard card">
                    <h3>{it.t}</h3>
                    <p>{it.d}</p>
                    <span className="format__link">Read guide →</span>
                  </Link>
                ))}
              </div>
            </div>
          ))}

          <div className="guide__prose guides-hub__outro">
            <h2>The questions we get asked most</h2>
            <p>
              These come up constantly in support email, and each has a guide behind it:
            </p>
            <ul>
              <li>
                <strong>&quot;Why won&apos;t my EPS open?&quot;</strong> Your computer has no
                PostScript interpreter. Open it from inside compatible software, or use an
                online viewer — see{" "}
                <Link to="/guides/how-to-open-eps-files">How to Open EPS Files</Link>.
              </li>
              <li>
                <strong>&quot;Why is my logo blurry?&quot;</strong> You are almost certainly
                seeing the embedded low-resolution preview thumbnail rather than the real
                artwork. <Link to="/guides/fix-eps-file-problems">Fix Common EPS Problems</Link>{" "}
                explains how to tell.
              </li>
              <li>
                <strong>&quot;Why did my transparent background turn white?&quot;</strong>{" "}
                JPG cannot store transparency at all, and EPS does not support live
                transparency either. Use{" "}
                <Link to="/guides/convert-eps-to-png">PNG</Link> instead.
              </li>
              <li>
                <strong>&quot;Do I need Illustrator?&quot;</strong> No. See{" "}
                <Link to="/guides/open-eps-without-illustrator">Open EPS Without Illustrator</Link>{" "}
                for the free alternatives and what each can actually do.
              </li>
              <li>
                <strong>&quot;What should I send my printer?&quot;</strong> Usually a
                print-ready <Link to="/guides/convert-eps-to-pdf">PDF</Link> — most modern
                print workflows prefer it over EPS.
              </li>
            </ul>

            <h2>How these guides are written</h2>
            <p>
              We build and maintain EPS Viewer, so these guides come out of the problems
              our own users actually hit rather than from keyword lists. Where a guide
              describes a piece of software, we have run the steps ourselves; where
              behaviour differs between Windows, macOS and Linux, we say so instead of
              pretending there is one answer.
            </p>
            <p>
              Operating systems and applications change, and EPS support in particular has
              been quietly removed from several products over the years. Each guide carries
              the date it was last reviewed. If you find something out of date or simply
              wrong, <Link to="/contact">tell us</Link> — we would rather fix it.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
}
